import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import LegalPage from "./LegalPage";
import { BUSINESS } from "@/lib/business";

const CONTENT = {
  ar: {
    eyebrow: "قانوني",
    title: "سياسة إلغاء الاشتراك",
    updatedAt: "٢٤ فبراير ٢٠٢٦",
    intro:
      "يمكنك إلغاء التجديد التلقائي لاشتراكك في DAAM في أي وقت. لن يؤدي الإلغاء إلى حذف حسابك، وستبقى محتفظاً بالوصول إلى الميزات المدفوعة حتى نهاية الدورة المدفوعة الحالية.",
    sections: [
      { title: "١. ما الذي يعنيه الإلغاء",
        list: [
          "إيقاف التجديد التلقائي المقبل — لن تُخصم بطاقتك مرة أخرى.",
          "استمرار الوصول الكامل إلى الميزات المدفوعة حتى انتهاء الدورة الحالية التي دفعت ثمنها.",
          "احتفاظ الحساب بجميع بياناته: المحتوى المُولَّد، سجل المحادثات، والحسابات الاجتماعية المرتبطة.",
        ]},
      { title: "٢. ما الذي لا يعنيه الإلغاء",
        paragraphs: ["الإلغاء ليس حذفاً للحساب. إذا كنت ترغب في حذف الحساب نهائياً، يُرجى استخدام خيار \"حذف الحساب\" من صفحة الملف الشخصي أو مراسلة الدعم."],
      },
      { title: "٣. كيف تُلغي",
        paragraphs: ["يمكنك الإلغاء عبر أحد الخيارات التالية:"],
        list: [
          "من داخل التطبيق: \"الفوترة\" ← \"إلغاء التجديد\".",
          "بمراسلة الدعم عبر صفحة \"تواصل معنا\" مع اختيار فئة \"إلغاء\".",
          "بالردّ على أي بريد إلكتروني تلقائي مرسل من DAAM قبل موعد التجديد.",
        ]},
      { title: "٤. متى يبدأ سريان الإلغاء",
        paragraphs: [
          "يسري الإلغاء فوراً على التجديد المقبل. مثال: إذا كانت دورتك الحالية تنتهي في ١٥ من الشهر التالي، ستبقى مُشتركاً كاملاً حتى ١٥، وسيتوقف الوصول إلى الميزات المدفوعة تلقائياً بعد هذا التاريخ.",
        ]},
      { title: "٥. الفترة التجريبية والإلغاء",
        paragraphs: [
          "خلال الفترة التجريبية (٧ أيام) يمكنك الإلغاء في أي وقت. عند الإلغاء داخل الفترة التجريبية، لن يتم خصم رسوم الاشتراك الأولى.",
          "أي رسم تحقّق رمزي (١ ريال سعودي) لتفويض البطاقة يُعاد أو يُحرَّر تلقائياً من قِبَل Tap Payments وفق سياسة البنك المُصدِر (٥ إلى ١٤ يوم عمل).",
        ]},
      { title: "٦. إعادة الاشتراك",
        paragraphs: [
          "يمكنك إعادة تفعيل الاشتراك في أي وقت بعد الإلغاء عبر صفحة \"الخطط\" داخل التطبيق. سيبدأ اشتراكك الجديد من تاريخ الدفع، بدورة فوترة جديدة.",
        ]},
      { title: "٧. الاسترداد بعد الإلغاء",
        paragraphs: [
          "الإلغاء لا يمنح استرداداً تلقائياً للدورة الحالية. راجع سياسة الاسترداد لمعرفة الحالات التي قد تُستحق فيها استرداداً.",
        ]},
      { title: "٨. التواصل معنا",
        paragraphs: [`لأي استفسار حول الإلغاء: ${BUSINESS.email_support}`]},
    ],
  },
  en: {
    eyebrow: "Legal",
    title: "Subscription Cancellation Policy",
    updatedAt: "February 24, 2026",
    intro:
      "You may cancel your DAAM auto-renewal at any time. Cancellation does not delete your account, and you keep full access to paid features until the end of the current paid billing period.",
    sections: [
      { title: "1. What cancellation does",
        list: [
          "Stops the upcoming automatic renewal — your card will not be charged again.",
          "Preserves your full access to paid features until the end of the current paid cycle.",
          "Keeps all your data: generated content, chat history and connected social accounts.",
        ]},
      { title: "2. What cancellation does NOT do",
        paragraphs: ["Cancellation is not account deletion. If you want to permanently delete your account, please use the \"Delete Account\" option in your profile page or contact support."],
      },
      { title: "3. How to cancel",
        paragraphs: ["You may cancel via any of the following:"],
        list: [
          "In-app: Billing → Cancel renewal.",
          "By contacting support through the \"Contact us\" page with the category \"Cancellation\".",
          "By replying to any automated email DAAM sends before the renewal date.",
        ]},
      { title: "4. When cancellation takes effect",
        paragraphs: [
          "Cancellation takes effect immediately for the next renewal. Example: if your current cycle ends on the 15th of next month, you keep full access until the 15th, and paid-feature access stops automatically after that date.",
        ]},
      { title: "5. Trial period and cancellation",
        paragraphs: [
          "During the 7-day trial you may cancel at any time. When you cancel inside the trial, the first subscription charge is not taken.",
          "Any nominal card-verification amount (1 SAR) is released or refunded automatically by Tap Payments according to the issuing bank's policy (5-14 business days).",
        ]},
      { title: "6. Re-subscribing",
        paragraphs: [
          "You can reactivate at any time after cancellation via the Plans page inside the app. Your new subscription starts on the payment date with a fresh billing cycle.",
        ]},
      { title: "7. Refund after cancellation",
        paragraphs: [
          "Cancellation does not automatically grant a refund for the current cycle. See the Refund Policy for cases where a refund may be considered.",
        ]},
      { title: "8. Contact us",
        paragraphs: [`For any cancellation enquiry: ${BUSINESS.email_support}`]},
    ],
  },
};

export default function CancellationPage() {
  const { lang } = useI18n();
  const c = CONTENT[lang] || CONTENT.en;
  return (
    <LegalPage testId="cancellation-page" eyebrow={c.eyebrow} title={c.title}
      updatedAt={c.updatedAt} intro={c.intro} sections={c.sections} />
  );
}
